import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  Menu,
  X,
  Globe,
  ShieldCheck,
  QrCode,
  ChevronDown,
  ChevronUp,
  Bell,
  Lock,
  Wallet,
  Plus,
  Banknote,
  Zap,
  Users,
  FileText,
  RefreshCcw,
  CreditCard,
  Info,
  Star,
  Fingerprint,
  MessageCircle,
  Phone,
  Mail,
  HelpCircle,
} from "lucide-react";

import VQrCode from "../../components/VQrCode/VQrCode";
import MobileAppHead from "../../components/MobileAppHead/MobileAppHead";
import {
  MOBILE_APP,
  getAppDownloadUrl,
  getPersonalQrLink,
} from "../../config/mobileApp";
import classes from "./Deprecated.module.scss";

// Logo
import logoImg from "../../assets/images/logo/fincompay-logo.png";

// Phone screens for carousel (order for display)
import phoneScreen1 from "../../assets/images/phone-screens/1.png";
import phoneScreen2 from "../../assets/images/phone-screens/2.png";
import phoneScreen3 from "../../assets/images/phone-screens/2.1.png";
import phoneScreen4 from "../../assets/images/phone-screens/3.png";
import phoneScreen5 from "../../assets/images/phone-screens/3.1.png";
import phoneScreen6 from "../../assets/images/phone-screens/4.png";
import phoneScreen7 from "../../assets/images/phone-screens/5.png";
import phoneScreen8 from "../../assets/images/phone-screens/6.png";
import phoneScreen9 from "../../assets/images/phone-screens/7.png";
import phoneScreen10 from "../../assets/images/phone-screens/8.png";

// Store / Play buttons (iOS: SVG per language; Android: PNG per language)
import iosRoImg from "../../assets/images/ios/ios_ro.svg";
import iosRuImg from "../../assets/images/ios/ios_ru.svg";
import iosEnImg from "../../assets/images/ios/ios_en.svg";
import androidRoImg from "../../assets/images/android/android-ro.png";
import androidRuImg from "../../assets/images/android/android-ru.png";
import androidEnImg from "../../assets/images/android/android-en.png";

const PHONE_SCREENS = [
  phoneScreen1,
  phoneScreen2,
  phoneScreen3,
  phoneScreen4,
  phoneScreen5,
  phoneScreen6,
  phoneScreen7,
  phoneScreen8,
  phoneScreen9,
  phoneScreen10,
];

const FOOTER_PRODUCT_URLS = [
  "https://fincombank.com/ro/debetovye-karty#filter=.all",
  "https://fincombank.com/ro/credite-pentru-tine#filter=.all",
  "https://fincombank.com/ro/tipurile-de-depozite#filter=.online",
];
const FOOTER_LEGAL_URLS = [
  "https://fincompay.com/resources/b949bd7a44043bdcc856d393d20086az.pdf",
  "https://fincombank.com/resource/FCB/condi%C8%9Biile_de_prelucrare_%C8%99i_protec%C8%9Bie_a_datelor_cu_caracter_personal.pdf",
  "https://fincombank.com/ro/tarife-fiz",
  "https://fincombank.com/ro/security/",
];
const FOOTER_COMPANY_URLS = [
  "https://fincombank.com/ro/despre-noi",
  "https://fincombank.com/ro/filiale-si-agentii",
  "https://fincombank.com/ro/cariera",
];

const ICON_MAP = {
  Clock: RefreshCcw,
  Bell: Bell,
  Lock: Lock,
  Wallet: Wallet,
  Card: CreditCard,
  Plus: Plus,
  Banknote: Banknote,
  Globe: Globe,
  FileText: FileText,
  Zap: Zap,
  QrCode: QrCode,
  Users: Users,
  Refresh: RefreshCcw,
} as unknown as Record<string, React.ComponentType<{ size?: number }>>;

const FadeInSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.1 },
    );
    const { current } = domRef;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`${classes.fadeIn} ${isVisible ? classes.visible : classes.hidden}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const AccordionItem: React.FC<{ question: string; answer: string }> = ({
  question,
  answer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={classes.accordionItem}>
      <button
        type="button"
        className={classes.accordionBtn}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        {isOpen ? (
          <ChevronUp
            size={20}
            style={{ color: "var(--teal-landing, #2ab7a9)" }}
          />
        ) : (
          <ChevronDown size={20} className={classes.accordionAnswer} />
        )}
      </button>
      <div
        className={`${classes.accordionContent} ${isOpen ? classes.open : classes.closed}`}
      >
        <p className={classes.accordionAnswer}>{answer}</p>
      </div>
    </div>
  );
};

const Deprecated: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const lang = (i18n.language || "ro").split("-")[0];
  const iosBtnImg =
    lang === "ro" ? iosRoImg : lang === "ru" ? iosRuImg : iosEnImg;
  const androidBtnImg =
    lang === "ro" ? androidRoImg : lang === "ru" ? androidRuImg : androidEnImg;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Chat widget (fincombank.com): Support Board requires jQuery; then SB_DEFAULT_DEPARTMENT + main.js
  useEffect(() => {
    const win = window as Window & { SB_DEFAULT_DEPARTMENT?: number };
    win.SB_DEFAULT_DEPARTMENT = 2;

    if (document.getElementById("sbinit")) return;

    const loadChat = () => {
      const script = document.createElement("script");
      script.id = "sbinit";
      script.src = `https://chat.fincombank.com/js/main.js?lang=${lang}&v=1.0.1`;
      script.async = false;
      document.head.appendChild(script);
    };

    if (
      typeof (window as Window & { jQuery?: unknown }).jQuery !== "undefined"
    ) {
      loadChat();
      return;
    }

    if (document.getElementById("sb-jquery")) return;

    const jq = document.createElement("script");
    jq.id = "sb-jquery";
    jq.src = "https://code.jquery.com/jquery-3.7.1.min.js";
    jq.crossOrigin = "anonymous";
    jq.onload = loadChat;
    document.head.appendChild(jq);
  }, [lang]);

  const featuresList = t("landing.features.list", {
    returnObjects: true,
  }) as Array<{ icon: string; title: string; desc: string }>;
  const securityCards = t("landing.security.cards", {
    returnObjects: true,
  }) as Array<{ title: string; desc: string }>;
  const faqItems = t("landing.faq.items", {
    returnObjects: true,
  }) as Array<{ q: string; a: string }>;
  const footerProducts = t("landing.footer.products", {
    returnObjects: true,
  }) as string[];
  const footerLegal = t("landing.footer.legal", {
    returnObjects: true,
  }) as string[];
  const footerCompany = t("landing.footer.company", {
    returnObjects: true,
  }) as string[];

  return (
    <div className={classes.root}>
      <MobileAppHead />
      <nav
        className={`${classes.nav} ${scrolled ? classes.scrolled : ""}`}
        style={{ padding: scrolled ? undefined : "1.5rem 0" }}
      >
        <div className={classes.navInner}>
          <Link to="/" className={classes.logoWrap}>
            <img src={logoImg} alt="FinComPay" className={classes.logoImg} />
          </Link>

          <div className={classes.desktopNav}>
            <a href="#features">{t("landing.nav.features")}</a>
            <a href="#security">{t("landing.nav.security")}</a>
            <a href="#support">{t("landing.nav.support")}</a>
            <a href="#faq">{t("landing.nav.faq")}</a>
            <div className={classes.langWrap}>
              {["ro", "ru", "en"].map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`${classes.langBtn} ${lang === l ? classes.active : ""}`}
                  onClick={() => i18n.changeLanguage(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href={getAppDownloadUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.downloadBtn}
            >
              {t("landing.nav.download")}
            </a>
          </div>

          <button
            type="button"
            className={classes.menuBtn}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <div
          className={`${classes.mobileNav} ${isMenuOpen ? classes.open : classes.closed}`}
        >
          <div className={classes.mobileNavInner}>
            <a href="#features" onClick={() => setIsMenuOpen(false)}>
              {t("landing.nav.features")}
            </a>
            <a href="#security" onClick={() => setIsMenuOpen(false)}>
              {t("landing.nav.security")}
            </a>
            <a href="#support" onClick={() => setIsMenuOpen(false)}>
              {t("landing.nav.support")}
            </a>
            <a href="#faq" onClick={() => setIsMenuOpen(false)}>
              {t("landing.nav.faq")}
            </a>
            <div className={`${classes.langWrap} ${classes.mobileLangWrap}`}>
              {["ro", "ru", "en"].map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`${classes.langBtn} ${lang === l ? classes.active : ""}`}
                  onClick={() => i18n.changeLanguage(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href={getAppDownloadUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.mobileDownloadBtn}
            >
              {t("landing.nav.download")}
            </a>
          </div>
        </div>
      </nav>

      <section className={classes.hero}>
        <div className={classes.heroDecor1} aria-hidden />
        <div className={classes.heroDecor2} aria-hidden />
        <div className={classes.heroGrid}>
          <div className={classes.heroTextCol}>
            <FadeInSection>
              <div className={classes.heroContent}>
                <div className={classes.notifBox}>
                  <Info size={20} style={{ color: "#2ab7a9", flexShrink: 0 }} />
                  <div className={classes.notifText}>
                    <span className={classes.notifTitle}>
                      {t("landing.hero.notification_title")}
                    </span>
                    {t("landing.hero.notification_text")}
                  </div>
                </div>
                <h1 className={classes.heroTitle}>
                  {t("landing.hero.title1")} <br />
                  <span className={classes.heroTitle2}>
                    {t("landing.hero.title2")}
                  </span>
                </h1>
                <p className={classes.heroDesc}>{t("landing.hero.desc")}</p>
                <div className={classes.appButtons}>
                  <a
                    href={getAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.appBtnLink}
                  >
                    <img
                      src={iosBtnImg}
                      alt="App Store"
                      className={classes.appBtnImg}
                    />
                  </a>
                  <div className={classes.qrCard}>
                    <span className={classes.qrTooltip}>
                      {t("landing.cta.scan")}
                    </span>
                    <div
                      style={{
                        background: "#fff",
                        padding: 2,
                        borderRadius: 4,
                      }}
                    >
                      <VQrCode
                        link={getPersonalQrLink()}
                        size={90}
                        color="#000"
                        bgColor="#fff"
                      />
                    </div>
                  </div>
                  <a
                    href={getAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.appBtnLink}
                  >
                    <img
                      src={androidBtnImg}
                      alt="Google Play"
                      className={classes.appBtnImg}
                    />
                  </a>
                </div>
                <div className={classes.rating}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={16}
                      fill="currentColor"
                      style={{ opacity: i === 5 ? 0.7 : 1 }}
                    />
                  ))}
                  <span className={classes.ratingText}>
                    {t("landing.hero.rating")}
                  </span>
                </div>
              </div>
            </FadeInSection>
          </div>

          <div className={classes.heroCarouselCol}>
            <div className={classes.screensCarouselWrap}>
              <Swiper
                modules={[Autoplay, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                loop
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                  bulletClass: classes.swiperBullet,
                  bulletActiveClass: classes.swiperBulletActive,
                }}
                className={classes.screensCarousel}
              >
                {PHONE_SCREENS.map((src, i) => (
                  <SwiperSlide key={i}>
                    <img
                      src={src}
                      alt=""
                      className={classes.screensCarouselImg}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </section>

      <section
        id="features"
        className={`${classes.section}`}
        style={{ background: "#f8fafc" }}
      >
        <div
          style={{ maxWidth: "80rem", margin: "0 auto", padding: "0 1.5rem" }}
        >
          <FadeInSection delay={200}>
            <div
              style={{
                textAlign: "center",
                maxWidth: "42rem",
                margin: "0 auto 4rem",
              }}
            >
              <h2 className={classes.sectionTitle}>
                {t("landing.features.title")}
              </h2>
              <p className={classes.sectionSubtitle}>
                {t("landing.features.subtitle")}
              </p>
            </div>
          </FadeInSection>
          <div className={classes.featuresGrid}>
            {featuresList.map((feature, idx) => {
              const Icon = ICON_MAP[feature.icon] || Lock;
              return (
                <FadeInSection key={idx} delay={idx * 50}>
                  <div className={classes.featureCard}>
                    <div className={classes.featureIcon}>
                      <Icon size={24} />
                    </div>
                    <div>
                      <h3 className={classes.featureTitle}>{feature.title}</h3>
                      <p className={classes.featureDesc}>{feature.desc}</p>
                    </div>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <span className={classes.moreBtn}>
              {t("landing.features.more")}
            </span>
          </div>
        </div>
      </section>

      <section
        id="security"
        className={`${classes.section} ${classes.securitySection}`}
      >
        <div
          style={{ maxWidth: "80rem", margin: "0 auto", padding: "0 1.5rem" }}
        >
          <FadeInSection>
            <div style={{ textAlign: "center", marginBottom: "3rem" }}>
              <div className={classes.securityBadge}>
                <ShieldCheck size={14} />
                <span>{t("landing.nav.security")}</span>
              </div>
              <h2 className={classes.sectionTitle}>
                {t("landing.security.title")}
              </h2>
              <p className={classes.sectionSubtitle}>
                {t("landing.security.subtitle")}
              </p>
            </div>
          </FadeInSection>
          <div className={classes.securityGrid}>
            {securityCards.map((card, idx) => (
              <FadeInSection key={idx} delay={idx * 100}>
                <div className={classes.securityCard}>
                  <div className={classes.securityCardIcon}>
                    {idx === 0 && (
                      <Lock size={24} style={{ color: "#2ab7a9" }} />
                    )}
                    {idx === 1 && (
                      <Fingerprint size={24} style={{ color: "#2ab7a9" }} />
                    )}
                    {idx === 2 && (
                      <ShieldCheck size={24} style={{ color: "#2ab7a9" }} />
                    )}
                  </div>
                  <h4 className={classes.securityCardTitle}>{card.title}</h4>
                  <p className={classes.securityCardDesc}>{card.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      <section id="support" className={classes.section}>
        <div
          style={{ maxWidth: "80rem", margin: "0 auto", padding: "0 1.5rem" }}
        >
          <FadeInSection>
            <div
              style={{
                textAlign: "center",
                maxWidth: "42rem",
                margin: "0 auto 4rem",
              }}
            >
              <h2 className={classes.sectionTitle}>
                {t("landing.support.title")}
              </h2>
              <p className={classes.sectionSubtitle}>
                {t("landing.support.subtitle")}
              </p>
            </div>
          </FadeInSection>
          <div className={classes.supportGrid}>
            <FadeInSection delay={0}>
              <div className={classes.supportCard}>
                <div
                  className={classes.supportCardIcon}
                  style={{ background: "#dbeafe", color: "#2563eb" }}
                >
                  <MessageCircle size={32} />
                </div>
                <h3 className={classes.supportCardTitle}>
                  {t("landing.support.chat_title")}
                </h3>
                <p className={classes.supportCardDesc}>
                  {t("landing.support.chat_desc")}
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={100}>
              <div className={classes.supportCard}>
                <div
                  className={classes.supportCardIcon}
                  style={{ background: "#dcfce7", color: "#16a34a" }}
                >
                  <Phone size={32} />
                </div>
                <h3 className={classes.supportCardTitle}>
                  {t("landing.support.phone_title")}
                </h3>
                <p className={classes.supportCardValue}>+373 22-26-99-99</p>
              </div>
            </FadeInSection>
            <FadeInSection delay={200}>
              <div className={classes.supportCard}>
                <div
                  className={classes.supportCardIcon}
                  style={{ background: "#f3e8ff", color: "#9333ea" }}
                >
                  <Mail size={32} />
                </div>
                <h3 className={classes.supportCardTitle}>
                  {t("landing.support.email_title")}
                </h3>
                <p className={classes.supportCardValue}>
                  callcenter@fincombank.com
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      <section id="faq" className={classes.faqSection}>
        <div className={classes.faqWrap} style={{ padding: "0 1.5rem" }}>
          <FadeInSection>
            <div style={{ textAlign: "center", marginBottom: "3rem" }}>
              <div className={classes.faqBadge}>
                <HelpCircle size={14} />
                <span>FAQ</span>
              </div>
              <h2 className={classes.sectionTitle}>{t("landing.faq.title")}</h2>
            </div>
            <div className={classes.faqList}>
              {faqItems.map((item, i) => (
                <AccordionItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      <section className={classes.ctaSection}>
        <FadeInSection>
          <div className={classes.ctaBox}>
            <div className={classes.ctaPattern} aria-hidden />
            <div className={classes.ctaInner}>
              <div style={{ maxWidth: "36rem" }}>
                <h2 className={classes.ctaTitle}>{t("landing.cta.title")}</h2>
                <p className={classes.ctaDesc}>{t("landing.cta.desc")}</p>
                <div className={classes.ctaButtons}>
                  <a
                    href={getAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.ctaBtnLink}
                  >
                    <img
                      src={iosBtnImg}
                      alt="App Store"
                      className={classes.ctaBtnImg}
                    />
                  </a>
                  <a
                    href={getAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.ctaBtnLink}
                  >
                    <img
                      src={androidBtnImg}
                      alt="Google Play"
                      className={classes.ctaBtnImg}
                    />
                  </a>
                </div>
              </div>
              <div className={classes.ctaQr}>
                <div className={classes.ctaQrBox}>
                  <VQrCode
                    link={getPersonalQrLink()}
                    size={200}
                    color="#0f172a"
                    bgColor="#ffffff"
                  />
                </div>
                <div className={classes.ctaQrLabel}>
                  {t("landing.cta.scan")}
                </div>
              </div>
            </div>
          </div>
        </FadeInSection>
      </section>

      <footer className={classes.footer}>
        <div className={classes.footerInner}>
          <div className={classes.footerTop}>
            <div className={classes.footerBrand}>
              <div className={classes.footerLogoRow}>
                <img
                  src={logoImg}
                  alt="FinComPay"
                  className={classes.footerLogoImg}
                />
              </div>
              <p className={classes.footerService}>
                {t("landing.footer.service")}
              </p>
            </div>
            <div className={classes.footerCols}>
              <div>
                <h4 className={classes.footerColTitle}>
                  {t("landing.footer.col1")}
                </h4>
                <ul className={classes.footerColList}>
                  {footerProducts.map((label, i) => (
                    <li key={i}>
                      <a
                        href={FOOTER_PRODUCT_URLS[i]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className={classes.footerColTitle}>
                  {t("landing.footer.col2")}
                </h4>
                <ul className={classes.footerColList}>
                  {footerLegal.map((label, i) => (
                    <li key={i}>
                      <a
                        href={FOOTER_LEGAL_URLS[i]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className={classes.footerColTitle}>
                  {t("landing.footer.col3")}
                </h4>
                <ul className={classes.footerColList}>
                  {footerCompany.map((label, i) => (
                    <li key={i}>
                      <a
                        href={FOOTER_COMPANY_URLS[i]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className={classes.footerBottom}>
            <p>
              © {new Date().getFullYear()} FinComBank S.A.{" "}
              {t("landing.footer.rights")}
            </p>
            <div className={classes.footerSocial}>
              <a href="https://fincombank.com/" aria-label="Web">
                <Globe size={16} />
              </a>
              <a
                href="https://www.facebook.com/fincombank/"
                aria-label="Facebook"
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://ro.linkedin.com/company/fincombank-sa"
                aria-label="LinkedIn"
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Deprecated;
